import React, { ReactElement, useEffect } from "react";
import NextApp, { AppInitialProps, NextWebVitalsMetric } from "next/app";
import dynamic from "next/dynamic";
import "intersection-observer";
import getConfig from "next/config";
import { YMaps } from "react-yandex-maps";
import { UserAgentProvider } from "common/UserAgent";
import MindBox from "common/MindBox/MindBox";
import Analytics from "common/Analytics/Analytics";
import Favicons from "common/Favicons/Favicons";
import ProgressBar from "common/ProgressBar/ProgressBar";
import OAuth from "common/OAuth/OAuth";
import {
  StoreProvider,
  hydrateStore,
  preflightRequest,
  loadInitialStoreData,
} from "store";
import "assets/styles/globals.scss";
import {
  ApiProvider,
  createNextContextApi,
  createApi,
} from "services/ApiService";
import { createGrowthBook, GrowthBookProvider } from "services/GrowthBook";
import { ExtendedAppInitialProps, ExtendedAppProps } from "types/app";
import Errors from "pages/Errors/Errors";
import { getJaeger, getTraceId } from "helpers/telemetry";
import { YMAP_API_KEY } from "constants/maps";

const ClientEvents = dynamic(() => import("common/ClientEvents/ClientEvents"), {
  ssr: false,
});

const Notifications = dynamic(
  () => import("common/Notifications/Notifications"),
  {
    ssr: false,
  }
);

const { publicRuntimeConfig } = getConfig();

const gb = createGrowthBook();
const loadFeatures = async () => {
  try {
    await gb.loadFeatures({ autoRefresh: true });
  } catch (err) {
    console.warn("[GrowthBook Client] Error:", err.message);
  }
};

export function reportWebVitals(metric: NextWebVitalsMetric): void {
  publicRuntimeConfig.LOG_METRICS &&
    console.log("[Metrics][Web vitals]", metric);
}

export default function App({
  Component,
  pageProps,
  store,
  userAgent,
}: ExtendedAppProps): ReactElement {
  const universalApi = createApi();
  store.user?.token?.refreshToken &&
    universalApi.setTokens({
      access: store.user.token.accessToken,
      refresh: store.user.token.refreshToken,
    });

  useEffect(() => {
    loadFeatures();
  }, []);

  /* todo: remove after transition to getServerSideProps */
  /* Custom errors returned from getInitialProps, lightweight page */
  if (pageProps.err) {
    return (
      <UserAgentProvider ua={userAgent || "none;"}>
        <ApiProvider api={universalApi}>
          <StoreProvider store={hydrateStore(store, universalApi)}>
            <Errors statusCode={pageProps.err.statusCode} />
            <Notifications />
            <div id="tooltip-portal" />
          </StoreProvider>
        </ApiProvider>
      </UserAgentProvider>
    );
  }

  /* Next.js default error, lightweight page */
  if (Component?.displayName === "Errors") {
    return (
      <UserAgentProvider ua={userAgent || "none;"}>
        <ApiProvider api={universalApi}>
          <StoreProvider store={hydrateStore(store, universalApi)}>
            <Component {...pageProps} />
            <Notifications />
            <div id="tooltip-portal" />
          </StoreProvider>
        </ApiProvider>
      </UserAgentProvider>
    );
  }

  return (
    <UserAgentProvider ua={userAgent || "none;"}>
      <ApiProvider api={universalApi}>
        <StoreProvider store={hydrateStore(store, universalApi)}>
          <ProgressBar />
          <Analytics />
          <MindBox />
          <Favicons />
          <YMaps query={{ apikey: YMAP_API_KEY }}>
            <GrowthBookProvider growthbook={gb}>
              <OAuth />
              <Component {...pageProps} />
              <Notifications />
            </GrowthBookProvider>
            <ClientEvents />
          </YMaps>
          <div id="tooltip-portal" />
        </StoreProvider>
      </ApiProvider>
    </UserAgentProvider>
  );
}

App.getInitialProps = async (appContext): Promise<ExtendedAppInitialProps> => {
  const isFallbackPage = appContext.router.isFallback;
  const isNextSSRData = Boolean(appContext.ctx.req?.headers["x-nextjs-data"]);
  const userAgent = appContext.ctx.req
    ? appContext.ctx.req.headers["user-agent"]
    : navigator.userAgent;
  const traceId = getTraceId(appContext.ctx?.req);
  const jaeger = getJaeger(appContext.ctx?.req);

  createNextContextApi(appContext.ctx);

  /* Workaround for issue: https://github.com/vercel/next.js/issues/5400 */
  /* todo: try serversideProps & static and remove it */
  if (appContext.Component?.displayName === "Errors") {
    /* if is already error page, like 404 we need to suppress all another preload errors */
    const errorPreflightSpan = jaeger.createSpan(
      "_app[Error].preflightRequest"
    );
    try {
      await preflightRequest(appContext.ctx);
    } catch (err) {
      errorPreflightSpan.setTag("error", true);
      errorPreflightSpan.setTag("errors", err.toString());
      appContext.ctx.err = err;
    } finally {
      errorPreflightSpan.finish();
    }

    const appProps: AppInitialProps = await NextApp.getInitialProps(appContext);
    return {
      ...appProps,
      userAgent,
      traceId,
      store: isNextSSRData ? {} : appContext.ctx.store.toProps(),
    };
  }

  const preflightSpan = jaeger.createSpan("_app.preflightRequest");
  /* on error here Next.js catch and render error page */
  await preflightRequest(appContext.ctx);
  preflightSpan.finish();

  if (!isFallbackPage && !isNextSSRData) {
    const initialDataSpan = jaeger.createSpan("_app.loadInitialStoreData");
    await loadInitialStoreData(appContext.ctx);
    initialDataSpan.finish();
  }

  const componentPropsSpan = jaeger.createSpan("_app.NextApp.getInitialProps");
  const appProps: AppInitialProps = await NextApp.getInitialProps(appContext);
  componentPropsSpan.finish();

  return {
    ...appProps,
    userAgent,
    traceId,
    store: isNextSSRData ? {} : appContext.ctx.store.toProps(),
  };
};
